import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import DashboardCard from './DashboardCard';
import { budgetBand, formatTry, projectLabel } from '../constants';
import { Footnote } from '../styles';

// Proje bütçe kullanımı — bütçesi tanımlı projeler: onaylı harcama (dolu) + onay bekleyen
// (taralı) / bütçe. Tüm zamanlar (proje boyunca) — pencereyle sınırlı DEĞİL. Taahhüt edilen
// (onaylı + bekleyen) orana göre sıralanır: bütçeyi en çok zorlayan üstte.

const MAX_ROWS = 6;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
`;

const Name = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Numbers = styled.span`
  color: #6B7280;
  font-weight: 600;
  white-space: nowrap;
`;

const Track = styled.div`
  position: relative;
  height: 10px;
  border-radius: 5px;
  background: #EEF1F5;
  overflow: hidden;
`;

const Spent = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  border-radius: 5px;
  background: ${({ $color }) => $color};
`;

const Pending = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $from }) => Math.min(100, $from)}%;
  width: ${({ $from, $pct }) => Math.max(0, Math.min(100 - Math.min(100, $from), $pct))}%;
  background: repeating-linear-gradient(135deg, #f3b74b 0 4px, #fde7bd 4px 8px);
`;

const Pct = styled.span`
  font-weight: 800;
  color: ${({ $color }) => $color};
  margin-left: 6px;
`;

/// Satırlar (saf): bütçesi olan projeler, taahhüt oranına göre azalan.
export function buildBudgetRows(data) {
  return (data?.items || [])
    .filter((i) => i.projectId && i.budget > 0)
    .map((i) => ({
      ...i,
      spentPct: i.budgetUsagePct ?? 0,
      pendingPct: Math.max(0, (i.committedUsagePct ?? 0) - (i.budgetUsagePct ?? 0)),
    }))
    .sort((a, b) => (b.committedUsagePct ?? 0) - (a.committedUsagePct ?? 0));
}

export default function ProjectBudgetCard({ slice, filtersActive }) {
  const { t } = useTranslation();
  const history = useHistory();
  const data = slice.data;

  const rows = useMemo(() => buildBudgetRows(data), [data]);
  const over = data?.overBudgetCount || 0;

  return (
    <DashboardCard
      cardKey="proje-butce"
      title={t('expenseDashboard.projects.budgetTitle')}
      meta={over > 0
        ? t('expenseDashboard.projects.overBudgetMeta', { count: over })
        : t('expenseDashboard.projects.budgetMeta')}
      span={6}
      spanMd={6}
      loading={slice.loading}
      empty={rows.length === 0}
      emptyText={t('expenseDashboard.projects.noBudget')}
    >
      <div>
        <List>
          {rows.slice(0, MAX_ROWS).map((r) => {
            const color = budgetBand(r.spentPct);
            return (
              <Row
                key={r.projectId}
                type="button"
                data-testid={`budget-row-${r.projectId}`}
                onClick={() => history.push(`/all-expenses?projectId=${r.projectId}&projectName=${encodeURIComponent(r.name)}`)}
                title={t('expenseProject.usageTooltip', { spent: formatTry(r.lifetimeTotal), pending: formatTry(r.pendingTotal), budget: formatTry(r.budget) })}
              >
                <Head>
                  <Name>{projectLabel(r, t)}</Name>
                  <Numbers>
                    {`${formatTry(r.lifetimeTotal)} / ${formatTry(r.budget)}`}
                    <Pct $color={color}>{`%${r.spentPct}`}</Pct>
                  </Numbers>
                </Head>
                <Track>
                  <Spent $pct={r.spentPct} $color={color} />
                  {r.pendingPct > 0 && <Pending $from={r.spentPct} $pct={r.pendingPct} />}
                </Track>
              </Row>
            );
          })}
        </List>
        <Footnote>
          {t('expenseDashboard.projects.budgetNote')}
          {filtersActive ? ` ${t('expenseDashboard.projects.budgetFilteredNote')}` : ''}
        </Footnote>
      </div>
    </DashboardCard>
  );
}
